import type { Theme } from '../../contract.js';
import { auroraLight, auroraDark } from './variants.js';

/**
 * Aurora family — northern lights theme.
 *
 * Ships paired with the kWh-milestone unlock at 850 kWh (the
 * 3rd-tier reward in the post-rewrite unlock model). Family
 * registry entry exposes the theme to the picker; the
 * `skin_aurora` Unlock doc gates visibility via the milestone-
 * evaluator (Commit 1 of the unlock-model rewrite reactivated
 * the evaluator; Commit 2 staged the placeholder; this commit
 * flips `is_active` true via the seeds/unlocks.ts edit + the
 * --activate-aurora migration step).
 *
 * Charging animation: the aurora shader (shaderKind: 'aurora')
 * with per-mode variant (aurora-dark / aurora-light). The shader
 * source is adapted from the shadertoy "Lights in Smoke" demo
 * (Krzysztof Narkowicz pattern — Ashima simplex noise + six-
 * octave fbm clouds + three colored moving lights) with
 * uniform-injected palettes per variant.
 *
 * Map style: flat Mapbox light/dark (NOT satellite). Aurora is
 * a sky aesthetic; satellite imagery would compete with the
 * shader's atmospheric composition for visual attention. The
 * flat basemaps stay quiet and let the charging animation own
 * the visual identity.
 */
export const auroraTheme: Theme = {
  id: 'aurora',
  name: 'Aurora',
  description: 'Northern lights with emerald, magenta, and teal motion.',
  // Picker swatches — emerald + magenta + teal capture the
  // dark-variant aurora identity. The picker's per-row
  // SwatchRow renders from variant token fields directly;
  // this top-level array is read by packages/ui DriverThemePicker
  // for catalogue display.
  swatches: ['#1AF273', '#EB33B3', '#1A8FBF'],
  variants: { light: auroraLight, dark: auroraDark },
  // Aurora is milestone-gated, NOT default-unlocked. The
  // `unlockedByDefault: false` here matches the
  // `skin_aurora.is_active` state in the seed; gating logic
  // lives in the Unlock catalog + UserUnlock grants, not in
  // the registry. Setting this to true would NOT bypass the
  // unlock gate (the registry entry is purely structural —
  // unlock state is server-evaluated).
  unlockedByDefault: false,
  costPoints: 0,
  // Satellite-streets for both modes, matching the pattern
  // default / neon / obsidian / electric-light (and other shader
  // families) already follow. Satellite imagery has no real
  // "dark variant" — the same photo-real ground texture renders
  // regardless of mode. The mode toggle still flips chrome +
  // shader; only the map base stays constant.
  mapStyle: {
    light: 'mapbox://styles/mapbox/satellite-streets-v12',
    dark: 'mapbox://styles/mapbox/satellite-streets-v12',
  },
  chargingAnimation: 'shader',
  shaderKind: 'aurora',
  // Variant selector — Theme.shaderVariant is mode-dependent
  // for aurora. Picked dynamically by the dispatcher based on
  // the active ThemeProvider mode (light → aurora-light, dark
  // → aurora-dark). The registry entry below carries the dark
  // variant as the default; mode resolution at runtime overrides.
  shaderVariant: 'aurora-dark',
};
